import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CreditCard, Loader2, Check, X } from 'lucide-react';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { useCreditStore } from '../../stores/creditStore';
import { useProfileRole } from '../../hooks/useProfileRole';
import { formatPrice, calculateSavings } from '../../types/credits';
import type { ModalParams } from '../../utils/modalUrlParams';
import { buildReturnUrlWithModal } from '../../utils/modalUrlParams';

interface CreditsModalProps {
  onClose: () => void;
  variant?: 'standard' | 'paymentSuccessful' | 'paymentFailed';
  modalParams?: ModalParams | null;
}

const CreditsModal: React.FC<CreditsModalProps> = ({
  onClose,
  variant: initialVariant = 'standard',
  modalParams,
}) => {
  const { t } = useTranslation(['credits', 'common']);
  const location = useLocation();
  const {
    balance,
    packages,
    refreshBalance,
    fetchPackages,
    createCheckoutSession,
    loadingBalance,
    loadingPackages,
    loadingCheckout,
    error,
  } = useCreditStore();
  const { isFreelancer, isRecruiter } = useProfileRole();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [currentVariant, setCurrentVariant] = useState(initialVariant);

  useEffect(() => {
    refreshBalance();
    fetchPackages();
  }, [fetchPackages, refreshBalance]);

  const handleBuyCredits = async () => {
    if (!selectedProductId || !selectedPriceId) return;

    try {
      const baseUrl = location.pathname;
      const returnUrl = buildReturnUrlWithModal(baseUrl, modalParams || null);
      const checkoutUrl = await createCheckoutSession(
        selectedProductId,
        selectedPriceId,
        returnUrl
      );

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    }
  };

  const handleRetry = () => {
    setCurrentVariant('standard');
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-md p-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4">
        <h2 className="text-xl font-bold text-secondary">{t('wallet.title', 'Credit Balance')}</h2>
        <button
          className="flex h-8 w-8 items-center justify-center rounded-full text-secondary/40 hover:text-secondary hover:bg-secondary/5 transition-all"
          onClick={onClose}
          aria-label={t('common:form_actions.close', 'Close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Gradient divider */}
      <div className="h-px mx-0 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="px-6 pb-6 pt-4">
        <div className="bg-primary/5 rounded-2xl p-6 mb-4 text-center">
          <p className="text-sm text-secondary/60 mb-1">
            {t('wallet.availableCredits', 'Available Credits')}
          </p>
          <span className="text-5xl font-bold text-secondary">
            {loadingBalance ? '...' : (balance ?? 0)}
          </span>
        </div>

        {currentVariant === 'paymentSuccessful' && (
          <>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl">
              <p className="text-green-700">
                {t(
                  'wallet.paymentSuccessMessage',
                  'Votre paiement a été effectué avec succès. Vos crédits ont été ajoutés à votre Porte-monnaie'
                )}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button variant="primary" onClick={onClose} className="w-full">
                {t('common:form_actions.close', 'Close')}
              </Button>
            </div>
          </>
        )}

        {currentVariant === 'paymentFailed' && (
          <>
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
              <p className="text-red-700">
                {t('wallet.paymentFailedMessage', 'Le paiement a échoué. Veuillez réessayer.')}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button variant="primary" onClick={handleRetry} className="w-full">
                {t('wallet.retry', 'Retry')}
              </Button>
              <Button variant="transparent-with-border" onClick={onClose} className="w-full">
                {t('common:form_actions.cancel', 'Cancel')}
              </Button>
            </div>
          </>
        )}

        {currentVariant === 'standard' && (
          <>
            <p className="text-secondary/60 text-sm mb-4">
              {isFreelancer
                ? t(
                    'wallet.modalDescriptionFreelancer',
                    'Purchase credits to accept quote requests and bid on public projects.'
                  )
                : isRecruiter
                  ? t(
                      'wallet.modalDescriptionRecruiter',
                      'Purchase credits to post jobs and attract qualified freelancers.'
                    )
                  : t('wallet.modalDescription', 'Purchase credits to unlock premium features.')}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {packages.length === 0 && !loadingPackages ? (
              <div className="text-center py-4 mb-4">
                <p className="text-secondary/50 text-sm">
                  {t('wallet.noPackages', 'No credit packages available at the moment.')}
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <p className="text-sm font-semibold text-secondary mb-3">
                  {t('wallet.selectPackage', 'Select a package')}:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {packages.map((pkg, index) => {
                    const savings = calculateSavings(pkg.credits, pkg.price_cents);
                    let discount = 0;
                    if (index > 0 && packages.length > 0) {
                      const basePackage = packages[0];
                      const basePricePerCredit = basePackage.price_cents / basePackage.credits;
                      const currentPricePerCredit = pkg.price_cents / pkg.credits;
                      if (currentPricePerCredit < basePricePerCredit) {
                        discount =
                          ((basePricePerCredit - currentPricePerCredit) / basePricePerCredit) * 100;
                        discount = Math.round(discount);
                      }
                    }
                    const isSelected =
                      selectedProductId === pkg.stripe_product_id &&
                      selectedPriceId === pkg.stripe_price_id;

                    return (
                      <button
                        key={`${pkg.stripe_product_id}-${pkg.stripe_price_id}`}
                        onClick={() => {
                          setSelectedProductId(pkg.stripe_product_id);
                          setSelectedPriceId(pkg.stripe_price_id);
                        }}
                        className={`relative p-4 border-2 rounded-2xl text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-primary/20 hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3">
                            <div className="bg-primary rounded-full p-1">
                              <Check className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between pr-8">
                          <div>
                            <div className="text-lg font-bold text-secondary">
                              {pkg.credits} {t('wallet.credits', 'credits')}
                            </div>
                            {pkg.name && (
                              <div className="text-sm text-secondary/50 mt-1">{pkg.name}</div>
                            )}
                            {discount > 0 && (
                              <div className="text-xs text-green-600 font-medium mt-1">
                                {t('wallet.discount', 'Save')} {discount}%{' '}
                                {t('wallet.comparedToBase', 'vs base package')}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-primary">
                              {formatPrice(pkg.price_cents)}
                            </div>
                            {savings > 0 && (
                              <div className="text-xs text-green-600 font-medium">
                                {t('wallet.save', 'Save')} {savings}%
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                variant="primary"
                onClick={handleBuyCredits}
                disabled={
                  !selectedProductId || !selectedPriceId || loadingCheckout || packages.length === 0
                }
                className="w-full flex items-center justify-center gap-2"
              >
                {loadingCheckout ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('wallet.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    {t('wallet.buyCredits', 'Buy Credits')}
                  </>
                )}
              </Button>

              <Button variant="transparent-with-border" onClick={onClose} className="w-full">
                {t('common:form_actions.cancel', 'Cancel')}
              </Button>
            </div>
          </>
        )}
      </div>
    </FullBlockingModal>
  );
};

export default CreditsModal;
